import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PasswordInput } from "../../../../shared/components/password-input/password-input";
import { AppAutofocusDirective } from "../../../../shared/directives/appAutofocus.directive";
import { ProfileService } from '../../services/profile.service';
import { ProblemDetails } from "../../../../shared/components/problem-details/problem-details";
import { firstValueFrom } from 'rxjs';
import { PasswordChangedDTO } from '../../models/password-changed-dto';
import { MessageService } from 'primeng/api';
import { MessagingService } from '../../../../shared/services/messaging.service';

export type PasswordChangedCallback = (args: PasswordChangedDTO) => Promise<void>;

@Component({
  selector: 'app-password-change-dialog',
  templateUrl: './password-change-dialog.html',
  styleUrl: './password-change-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PasswordInput, FormsModule, AppAutofocusDirective, ProblemDetails],
})
export class PasswordChangeDialog {
  readonly form = viewChild<NgForm>('form');
  readonly formValid = signal(false);
  readonly profileService = inject(ProfileService);
  readonly messagesService = inject(MessagingService);

  constructor(public activeModal: NgbActiveModal) {
    effect((onCleanup) => {
      const form = this.form();
      if (!form) {
        this.formValid.set(false);
        return;
      }

      const updateValidity = () => this.formValid.set(form.valid ?? false);
      updateValidity();

      const subscription = form.statusChanges?.subscribe(updateValidity);
      onCleanup(() => subscription?.unsubscribe());
    });
  }


  readonly currentPassword = signal('');
  readonly password = signal('');
  readonly confirmPassword = signal('');
  readonly passwordMatches = computed(() => this.password() === this.confirmPassword());
  readonly valid = computed(() => this.passwordMatches() && this.formValid());
  readonly showCurrentPassword = signal(true);
  passwordChangedCallback!: PasswordChangedCallback;

  close(result = false): void {
    this.activeModal.close(result);
  }

  async changePassword() {
    this.messagesService.validationErrors.set(null);
    if (!this.valid())
      return;

    try {
      const callback = this.passwordChangedCallback;

      await callback({
        confirmPassword: this.confirmPassword(),
        currentPassword: this.currentPassword(),
        password: this.password()
      });

      this.close(true);

    }
    catch (error: any) {
      error.senderName = 'password-change';
      throw error;
    }
  }
}
